import {
  HHStack,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableContainer,
  HHTableHead,
  HHTableRow,
  HHTypography,
} from '@hinge-health/react-component-library';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton } from '@mui/material';
import { routes } from '../../constants/strings/routes';
import { BatchClientInsurerType } from '../../types';
import LoadingComponent from '../utils/loading';
import { useApplicabilityData } from './use-applicability-data';

// ACCOUNTS-AND-PLAN-MIGRATION: partnershipId, insurerId, clientId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
interface ProcedureCodeApplicabilityDrawerProps {
  partnershipId?: number | null;
  insurerId?: number | null;
  clientId?: number | null;
  cptCode: string;
}

export const ProcedureCodeApplicabilityDrawer = ({
  partnershipId,
  insurerId,
  clientId,
  cptCode,
}: ProcedureCodeApplicabilityDrawerProps): JSX.Element => {
  const { rows, loading, error } = useApplicabilityData(
    partnershipId,
    insurerId,
    clientId,
    cptCode,
  );

  if (loading) return <LoadingComponent center />;

  if (error) {
    return (
      <HHTypography hhVariant="body">
        Failed to load applicable client insurers.
      </HHTypography>
    );
  }

  if (rows.length === 0) {
    return (
      <HHTypography hhVariant="muted">
        No applicable client insurers found.
      </HHTypography>
    );
  }

  return (
    <HHStack direction="column" spacing={2} flex={1} minHeight={0}>
      <HHTableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <HHTable size="small" aria-label="Applicable client insurers">
          <HHTableHead>
            <HHTableRow>
              <HHTableCell>CI ID</HHTableCell>
              <HHTableCell>Client</HHTableCell>
              <HHTableCell>Insurer</HHTableCell>
              <HHTableCell>Partnership</HHTableCell>
              <HHTableCell>Status</HHTableCell>
              <HHTableCell padding="checkbox" />
            </HHTableRow>
          </HHTableHead>
          <HHTableBody>
            {rows.map((row: BatchClientInsurerType) => (
              <HHTableRow key={row.id}>
                <HHTableCell>{row.id}</HHTableCell>
                <HHTableCell>{row.client?.name ?? '--'}</HHTableCell>
                <HHTableCell>{row.insurer?.name ?? '--'}</HHTableCell>
                <HHTableCell>
                  {row.partnerships?.map(p => p.name).join(', ') || '--'}
                </HHTableCell>
                <HHTableCell>
                  {row.isActive ? 'Active' : 'Inactive'}
                </HHTableCell>
                <HHTableCell padding="checkbox">
                  <IconButton
                    size="small"
                    href={`${routes.billing.main}/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open client insurer"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </HHTableCell>
              </HHTableRow>
            ))}
          </HHTableBody>
        </HHTable>
      </HHTableContainer>
    </HHStack>
  );
};
